import type { Station } from './types';

/**
 * Every station was verified to be reporting wind on 2026-07-31 by direct request.
 *
 * Deliberately excluded:
 *   9414296 Pier 17 Visibility, 9415020 Point Reyes — no wind sensor (CO-OPS returns an error)
 *   46237 SF Bar — Scripps waverider, waves only, no anemometer
 *   FTPC1 / AAMC1 / LNDC1 / PPXC1 — NDBC re-publications of CO-OPS stations already
 *     listed below; the CO-OPS feed has finer cadence, deeper history, and CORS
 *   KSFO / KOAK and other airports — api.weather.gov returns null wind too often to trust
 */
/**
 * `sensors` was probed station-by-station against the live API on 2026-07-31.
 * Water level and tide predictions additionally require `datum=MLLW`; without it
 * CO-OPS reports the product as unavailable even where it exists.
 */
export const STATIONS: Station[] = [
  /*
   * --- Golden Gate Bridge Highway & Transportation District ---
   *
   * The only wind on this map measured on the Gate itself rather than a shore
   * or a hull, and the finest cadence here at five minutes with a gust. Their
   * district publishes it through a public Konect kiosk.
   *
   * Position is Golden Gate midspan, which is where the logger sits on their
   * own map. It is not in the feed — the kiosk's device endpoint takes a body
   * we could not determine — so this coordinate is ours, not theirs.
   */
  { id: 'GGB', name: 'Golden Gate Bridge', lat: 37.8199, lon: -122.4783, source: 'konect', kind: 'shore',
    sensors: {}, reportMinutes: 5 },

  // --- NOAA CO-OPS, inside the Bay. Browser-fetchable, 6-minute cadence, knots. ---
  { id: '9414290', name: 'San Francisco (Fort Point)', lat: 37.8063, lon: -122.4659, source: 'coops', kind: 'shore',
    sensors: { waterLevel: true, airTemp: true } },
  { id: '9414311', name: 'San Francisco Pier 1',       lat: 37.7980, lon: -122.3930, source: 'coops', kind: 'shore',
    sensors: { airTemp: true } },
  { id: '9414523', name: 'Redwood City',               lat: 37.5068, lon: -122.2119, source: 'coops', kind: 'shore',
    sensors: { waterLevel: true, airTemp: true } },
  { id: '9414750', name: 'Alameda',                    lat: 37.7720, lon: -122.3003, source: 'coops', kind: 'shore',
    sensors: { waterLevel: true, waterTemp: true, airTemp: true } },
  { id: '9414763', name: 'Oakland Berth 67',           lat: 37.7950, lon: -122.2830, source: 'coops', kind: 'shore',
    sensors: { airTemp: true } },
  { id: '9414769', name: 'Oakland Middle Harbor',      lat: 37.8000, lon: -122.3300, source: 'coops', kind: 'shore',
    sensors: {} },
  { id: '9414776', name: 'Oakland Berth 34',           lat: 37.8106, lon: -122.3331, source: 'coops', kind: 'shore',
    sensors: { airTemp: true } },
  { id: '9414847', name: 'Point Potrero Richmond',     lat: 37.9058, lon: -122.3650, source: 'coops', kind: 'shore',
    sensors: { airTemp: true } },
  { id: '9414863', name: 'Richmond',                   lat: 37.9283, lon: -122.4000, source: 'coops', kind: 'shore',
    sensors: { waterLevel: true, waterTemp: true, airTemp: true } },
  { id: '9415102', name: 'Martinez-Amorco Pier',       lat: 38.0346, lon: -122.1252, source: 'coops', kind: 'shore',
    sensors: { waterLevel: true, airTemp: true } },
  { id: '9415115', name: 'Pittsburg, Suisun Bay',      lat: 38.0416, lon: -121.8867, source: 'coops', kind: 'shore',
    sensors: { airTemp: true } },
  { id: '9415118', name: 'Union Pacific RR Bridge',    lat: 38.0383, lon: -122.1205, source: 'coops', kind: 'shore',
    sensors: {} },
  { id: '9415141', name: 'Davis Point',                lat: 38.0567, lon: -122.2596, source: 'coops', kind: 'shore',
    sensors: { airTemp: true } },
  { id: '9415144', name: 'Port Chicago',               lat: 38.0560, lon: -122.0395, source: 'coops', kind: 'shore',
    sensors: { waterLevel: true, waterTemp: true, airTemp: true } },

  // --- NDBC moored buoys, offshore. Proxied (no CORS), 10-minute cadence, m/s upstream. ---
  // Waves and temperatures ride in the same file as the wind, so they cost no
  // extra request. Wave height reports roughly hourly, hence the sparser series.
  { id: '46026', name: 'San Francisco, 18nm W', lat: 37.750, lon: -122.838, source: 'ndbc', kind: 'buoy',
    sensors: { waves: true, waterTemp: true } },
  // Transmits hourly, unlike the other three buoys' ten minutes — verified from
  // the gaps in its own realtime2 file. Without this it reads as permanently
  // stale and shows as a no-data ring.
  { id: '46012', name: 'Half Moon Bay',         lat: 37.356, lon: -122.881, source: 'ndbc', kind: 'buoy',
    reportMinutes: 60,
    sensors: { waves: true, waterTemp: true, airTemp: true } },
  { id: '46013', name: 'Bodega Bay',            lat: 38.242, lon: -123.301, source: 'ndbc', kind: 'buoy',
    sensors: { waves: true, airTemp: true } },
  { id: '46042', name: 'Monterey',              lat: 36.787, lon: -122.408, source: 'ndbc', kind: 'buoy',
    sensors: { waves: true, waterTemp: true, airTemp: true } },
];

/** Tide predictions are shown for the Golden Gate; SF is the reference station. */
export const TIDE_STATION = '9414290';

/** Golden Gate current station SFB1203, the reference for the Central Bay. */
const GATE = { lat: 37.8201, lon: -122.473 };

/**
 * Great-circle distance in nautical miles.
 */
export function nmFrom(lat: number, lon: number, to = GATE): number {
  const R_NM = 3440.065;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(to.lat - lat);
  const dLon = toRad(to.lon - lon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R_NM * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * How far the Gate's current predictions can honestly be stretched. Beyond the
 * Central Bay the stream is a different animal — South Bay and the Delta run on
 * their own timing — so the wind-against-tide read is withheld out there rather
 * than guessed.
 */
// Superseded by currentStationFor() in currentStations.ts: a single Gate radius
// applied the Gate's 257 deg ebb to stations whose stream sets 147 or 338, and
// got the wind-against-tide verdict wrong at 7 of the 8 stations it covered.

/**
 * The Golden Gate Bridge district's public kiosk for their bridge anemometer.
 *
 * Their page, not a NOAA one — the data is theirs under the terms it is
 * published on, so this is both the credit and the way to check the reading at
 * source.
 */
export const GGB_KIOSK_URL =
  'https://app.konectgds.com/kiosk/8ca22f6f-d9d5-4d2d-8c27-c363f23c3592';

export interface SourceLink {
  href: string;
  /** Names whoever actually publishes it — every station is not a NOAA one. */
  label: string;
}

/**
 * Where a station's reading comes from, and who to credit for it.
 *
 * Href and label are decided together on purpose. They were separate, with the
 * label hardcoded as "NOAA station page", so adding the bridge district's own
 * logger credited NOAA for data NOAA has nothing to do with.
 */
export function sourceLink(s: Station): SourceLink {
  switch (s.source) {
    case 'coops':
      return {
        href: `https://tidesandcurrents.noaa.gov/stationhome.html?id=${s.id}`,
        label: 'NOAA station page',
      };
    case 'konect':
      return { href: GGB_KIOSK_URL, label: 'Golden Gate Bridge District kiosk' };
    default:
      return {
        href: `https://www.ndbc.noaa.gov/station_page.php?station=${s.id}`,
        label: 'NOAA station page',
      };
  }
}
